<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAdmin } from 'dashboard/composables/useAdmin';

// T-145: a Tamara foi explícita, "para o agente e todos os perfis, não podemos ter a opção de
// excluir". O botão sai da tela para todo mundo, administrador incluído.
// ⚠️ A política do servidor continua permitindo o administrador, e isso é de propósito: é o que
// preserva o caminho de exclusão a pedido do titular (LGPD), que passa a ser feito fora da tela
// de atendimento. Está como pergunta aberta ao clube no documento de validação.
const EXCLUIR_CONTATO_NA_TELA = false;
import ContactInfoRow from './ContactInfoRow.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ContactDeleteModal from 'dashboard/modules/contact/ContactDeleteModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import SeloPlano from 'dashboard/components/widgets/conversation/fandata/SeloPlano.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    EditContact,
    Avatar,
    SocialIcons,
    ComposeConversation,
    ContactMergeModal,
    ContactDeleteModal,
    VoiceCallButton,
    InlineInput,
    SeloPlano,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['panelClose', 'abrirNotasDoContato'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
      EXCLUIR_CONTATO_NA_TELA,
    };
  },
  data() {
    return {
      showEditModal: false,
      isEditingName: false,
      editName: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      currentChat: 'getSelectedChat',
    }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    // FanData: CPF (custom attribute) em destaque no topo do painel, p/ o atendente ver rápido.
    cpf() {
      return this.contact.custom_attributes?.cpf || '';
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;

      const telegram = socialProfiles?.telegram || telegramUsername || '';
      const twitter = socialProfiles?.twitter || twitterScreenName || '';

      return {
        ...(socialProfiles || {}),
        twitter,
        telegram,
      };
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  methods: {
    dynamicTime,
    // COPIAR A FICHA INTEIRA DE UMA VEZ (pedido do clube, 13/10: "colocar um íconezinho para
    // copiar as informações do cliente, com o objetivo de facilitar para os agentes").
    //
    // O agente que abre um chamado no sistema do clube precisa dos quatro campos juntos, e
    // hoje copia um por um, trocando de janela entre cada um. Quatro idas e voltas viram uma.
    //
    // Só entra no texto o campo que TEM valor: linha com "CPF:" vazio colada num chamado é
    // pior que linha nenhuma, porque parece dado perdido em vez de dado não informado.
    dadosDoCliente() {
      return [
        [this.$t('CONTACT_PANEL.NOME'), this.contact.name],
        [this.$t('CONTACT_PANEL.CPF'), this.cpf],
        [this.$t('CONTACT_PANEL.EMAIL_ADDRESS'), this.contact.email],
        [this.$t('CONTACT_PANEL.PHONE_NUMBER'), this.contact.phone_number],
      ]
        .filter(([, valor]) => Boolean(valor))
        .map(([rotulo, valor]) => `${rotulo}: ${valor}`)
        .join('\n');
    },
    async copiarDadosDoCliente() {
      const texto = this.dadosDoCliente();
      if (!texto) {
        useAlert(this.$t('CONTACT_PANEL.COPIAR_DADOS_VAZIO'));
        return;
      }
      await copyTextToClipboard(texto);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    startEditingName() {
      this.editName = this.contact.name || '';
      this.isEditingName = true;
      this.$nextTick(() => {
        this.$refs.nameInput?.focus();
      });
    },
    saveNameEdit() {
      if (!this.isEditingName) return;
      this.isEditingName = false;
      const trimmed = this.editName.trim();
      if (trimmed && trimmed !== this.contact.name) {
        this.updateContactField({ name: trimmed });
      }
    },
    cancelNameEdit() {
      this.isEditingName = false;
    },
    onFieldUpdate(field, value) {
      this.updateContactField({ [field]: value });
    },
    async updateContactField(attrs) {
      const contactId = this.contact.id;
      try {
        await this.$store.dispatch('contacts/update', {
          id: contactId,
          ...attrs,
        });
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
        await this.$store.dispatch('contacts/fetchContactableInbox', contactId);
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          const detail = error.contactErrorDetail;
          if (detail) {
            useAlert(detail);
          } else {
            const invalidAttrs = Array.isArray(error.data) ? error.data : [];
            if (invalidAttrs.includes('email')) {
              useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
            } else if (invalidAttrs.includes('phone_number')) {
              useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
            } else {
              useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
            }
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(error.message || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
  },
};
</script>

<template>
  <!-- T-233 (24/09): fundo próprio, um cinza mais leve que o do painel, para separar à vista a
       área do CONTATO (a pessoa) da área do TICKET (o atendimento), que vêm logo abaixo. Sem
       isso a coluna inteira é um bloco só e o agente precisa ler para saber onde uma acaba. -->
  <div class="relative items-center w-full p-4 bg-n-alpha-1 border-b border-n-weak">
    <div class="flex flex-col w-full gap-2 text-left rtl:text-right">
      <div class="flex flex-row justify-between">
        <Avatar
          v-if="showAvatar"
          :src="contact.thumbnail"
          :name="contact.name"
          :status="contact.availability_status"
          :size="48"
          hide-offline-status
        />
      </div>

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <div v-if="showAvatar" class="flex items-center w-full min-w-0 gap-3">
          <InlineInput
            v-if="isEditingName"
            ref="nameInput"
            v-model="editName"
            custom-input-class="!text-base !font-medium"
            class="!w-fit"
            @enter-press="saveNameEdit"
            @escape-press="cancelNameEdit"
            @blur="saveNameEdit"
          />
          <h3
            v-else
            class="group/name flex-shrink max-w-full min-w-0 my-0 text-base capitalize break-words text-n-slate-12 cursor-pointer hover:text-n-slate-12/80"
            :title="$t('CONTACT_PANEL.CLICK_TO_EDIT')"
            @click="startEditingName"
          >
            {{ contact.name }}
            <span
              class="i-lucide-pencil text-xs text-n-slate-10 opacity-0 group-hover/name:opacity-100 transition-opacity ml-1 align-middle"
            />
          </h3>
          <div class="flex flex-row items-center gap-2">
            <span
              v-if="contact.created_at"
              v-tooltip.left="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                  contact.created_at
                )}`
              "
              class="i-lucide-info text-sm text-n-slate-10"
            />
            <a
              :href="contactProfileLink"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="leading-3"
            >
              <span class="i-lucide-external-link text-sm text-n-slate-10" />
            </a>
          </div>
        </div>
        <!-- Selo do Plano Camisa 7 (21/08): o vínculo aparece onde o agente já olha, sem
             precisar rolar até o Painel do Torcedor. A ficha completa continua lá embaixo. -->
        <SeloPlano
          :conversation-id="currentChat && currentChat.id"
          :cpf="String(cpf || '')"
        />

        <p v-if="additionalAttributes.description" class="break-words mb-0.5">
          {{ additionalAttributes.description }}
        </p>
        <div class="flex flex-col items-start w-full gap-2">
          <ContactInfoRow
            :value="cpf"
            icon="contact-identify"
            emoji="🪪"
            :title="$t('CONTACT_PANEL.CPF')"
            show-copy
            editable
            @update="
              value =>
                updateContactField({
                  custom_attributes: {
                    ...(contact.custom_attributes || {}),
                    cpf: value,
                  },
                })
            "
          />
          <ContactInfoRow
            :href="contact.email ? `mailto:${contact.email}` : ''"
            :value="contact.email"
            icon="mail"
            emoji="✉️"
            :title="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
            show-copy
            editable
            @update="value => onFieldUpdate('email', value)"
          />
          <ContactInfoRow
            :href="contact.phone_number ? `tel:${contact.phone_number}` : ''"
            :value="contact.phone_number"
            icon="call"
            emoji="📞"
            :title="$t('CONTACT_PANEL.PHONE_NUMBER')"
            show-copy
            editable
            @update="value => onFieldUpdate('phone_number', value)"
          />
          <ContactInfoRow
            v-if="contact.identifier"
            :value="contact.identifier"
            icon="contact-identify"
            emoji="🪪"
            :title="$t('CONTACT_PANEL.IDENTIFIER')"
          />
          <!-- T-227 (24/09, relatado de novo em 02/10): "Nome da empresa" saiu do cadastro do
               contato. A operação do clube atende TORCEDOR, não empresa. O campo continua fora,
               aqui e no modal de editar. Sai da tela, não do dado. -->
          <ContactInfoRow
            v-if="location || additionalAttributes.location"
            :value="location || additionalAttributes.location"
            icon="map"
            emoji="🌍"
            :title="$t('CONTACT_PANEL.LOCATION')"
          />
          <!-- O @ da rede social VOLTA (05/10). Ele tinha saído junto com a empresa em
               `7af70bbac`, e isso desfez o T-228, que é pedido dela de 24/09: o ícone sozinho
               não dizia QUAL perfil era, então o identificador passou a vir escrito ao lado.
               Os dois pedidos se cruzaram e eu resolvi para o lado errado. O que o T-227 pede é
               tirar o CAMPO do cadastro, e ele segue fora do formulário; o que o T-228 pede é
               MOSTRAR o @ de quem está do outro lado, e sem isso o agente atende alguém sem
               saber o perfil. Só aparece rede que existe: na prática, o Instagram. -->
          <SocialIcons :social-profiles="socialProfiles" />
        </div>
      </div>
      <div class="flex items-center w-full mt-0.5 gap-2">
        <ComposeConversation :contact-id="String(contact.id)">
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
              icon="i-ph-chat-circle-dots"
              slate
              faded
              sm
            />
          </template>
        </ComposeConversation>
        <VoiceCallButton
          :phone="contact.phone_number"
          :contact-id="contact.id"
          :conversation-id="currentChat?.id"
          icon="i-lucide-phone"
          sm
          faded
          slate
          :tooltip-label="$t('CONTACT_PANEL.CALL')"
        />
        <NextButton
          v-tooltip.top-end="$t('CONTACT_PANEL.COPIAR_DADOS')"
          icon="i-ph-copy"
          slate
          faded
          sm
          :aria-label="$t('CONTACT_PANEL.COPIAR_DADOS')"
          @click="copiarDadosDoCliente"
        />
        <NextButton
          v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="i-ph-pencil-simple"
          slate
          faded
          sm
          @click="toggleEditModal"
        />
        <!-- T-232 (24/09): as Notas do Contato saíram do corpo do painel, onde disputavam espaço
             com o que o agente escreve todo dia (a nota do TICKET), e viraram este botão. A nota
             da PESSOA é rara e importante; a do atendimento é frequente. -->
        <NextButton
          v-tooltip.top-end="$t('CONTACT_PANEL.ADICIONAR_NOTA_CONTATO')"
          icon="i-ph-note-pencil"
          slate
          faded
          sm
          :aria-label="$t('CONTACT_PANEL.ADICIONAR_NOTA_CONTATO')"
          @click="$emit('abrirNotasDoContato')"
        />
        <ContactMergeModal :primary-contact="contact">
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
              icon="i-ph-arrows-merge"
              slate
              faded
              sm
              :disabled="uiFlags.isMerging"
            />
          </template>
        </ContactMergeModal>
        <ContactDeleteModal
          v-if="EXCLUIR_CONTATO_NA_TELA"
          :contact="contact"
          @deleted="$emit('panelClose')"
        >
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
              icon="i-ph-trash"
              slate
              faded
              sm
              ruby
              :disabled="uiFlags.isDeleting"
            />
          </template>
        </ContactDeleteModal>
      </div>
      <EditContact
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />
    </div>
  </div>
</template>
